import {
  CustomDialog,
  Header,
  Content,
  Footer,
  Text,
  Image,
  ButtonGroup,
  Button,
  ActionButton,
  CloseButton,
  SkeletonCollection,
  Collection,
  CardView,
  Card,
  CardPreview,
  TooltipTrigger,
  Tooltip,
} from "@react-spectrum/s2";
import { useAsyncList } from "react-stately";
import { usePress } from "@react-aria/interactions";
import { useStore } from "zustand";
import { useEffect } from "react";
import PropTypes from "prop-types";

import { getBrands, getElements } from "@app/services/ccLibraries";
import { store } from "../../store";
import { useStory, useUpdateStory } from "@app/hooks/stories";

import { findPrimaryLogo } from "@app/utils";

import RefreshIcon from "@react-spectrum/s2/icons/Refresh";
import NoBrands from "@react-spectrum/s2/illustrations/linear/NoBrands";

import { style } from "@react-spectrum/s2/style" with { type: "macro" };

const BrandCard = ({ item }) => {
  return (
    <Card
      id={item.id}
      textValue={item.name}
      className={style({ minHeight: "[100%]" })}
    >
      <CardPreview>
        {item.type === "swatches" ? (
          <div
            className={style({
              boxSizing: "border-box",
              backgroundColor: "gray-100",
              size: "full",
              aspectRatio: "4/3",
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              padding: 24,
            })}
          >
            {item.swatches.map((swatch, index) => {
              const rgbColor = swatch?.[0]?.value
                ? `rgba(${swatch?.[0]?.value.r}, ${swatch?.[0]?.value.g}, ${swatch?.[0]?.value.b}, ${swatch?.[0]?.alpha || 1})`
                : "#000000";

              return (
                <div
                  key={index}
                  style={{ backgroundColor: rgbColor }}
                  className={style({
                    size: "full",
                  })}
                ></div>
              );
            })}
          </div>
        ) : (
          <Image
            src={item.url}
            alt={item.name}
            styles={style({
              boxSizing: "border-box",
              aspectRatio: "4/3",
              objectFit: "contain",
              padding: 24,
            })}
          />
        )}
      </CardPreview>
      <Content>
        <Text slot="title">{item.name}</Text>
      </Content>
    </Card>
  );
};

export const StoryBrandDialog = ({ storyId, onPrimaryAction }) => {
  const storyQuery = useStory(storyId);
  const story = storyQuery.data;

  const updateStory = useUpdateStory(storyId);

  const listData = useAsyncList({
    getKey: (item) => item.id,
    async load() {
      try {
        // { description, id, name, tags }
        const brands = await getBrands();

        const urls = await Promise.all(
          (brands ?? []).map(async (brand) => {
            // Find the primary logo element
            const logoToUse = await findPrimaryLogo(brand);

            const brandData = {
              name: brand.name,
              libraryId: brand["library_urn"],
              id: brand.id,
              url: logoToUse?.url,
              swatches: logoToUse?.swatches,
              type: logoToUse?.type,
            };

            return brandData;
          }),
        );

        urls.splice(0, 0, {
          name: "/NEW",
          id: "/NEW",
          url: "",
        });

        return {
          items: urls,
          selectedKeys: new Set(story?.brand ? [story?.brand?.id] : []),
        };
      } catch (error) {
        console.error("Failed to load styles:", error);
        return { items: [], selectedKeys: new Set() };
      }
    },
  });

  const handleSelectionChange = (selectedKeys) => {
    if (selectedKeys.has("/NEW")) {
      window.open("https://new.express.adobe.com/brands", "_blank");
      return;
    }

    listData.setSelectedKeys(selectedKeys);

    const brandReference = listData.getItem(selectedKeys.currentKey);

    // Save brandReference to story
    if (storyId && brandReference) {
      updateStory.mutate({
        brand: {
          libraryId: brandReference.libraryId,
          id: brandReference.id,
          name: brandReference.name,
        },
      });
    }
  };

  const renderCreateNewBrand = () => (
    <div
      className={style({
        size: "full",
        aspectRatio: "4/3",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
      })}
    >
      <NoBrands styles={style({ size: 48 })} />
      <Text
        styles={style({
          font: "ui-sm",
          textAlign: "center",
        })}
      >
        Create new brand in <br />
        <Text styles={style({ fontWeight: "bold" })}>Zoltar Demo Pilot</Text>
      </Text>
    </div>
  );

  const { pressProps } = usePress({
    onPress: () => {
      handleSelectionChange(new Set(["/NEW"]));
    },
  });

  const renderEmptyState = () => (
    <div {...pressProps} className={style({ width: 180, cursor: "pointer" })}>
      {renderCreateNewBrand()}
    </div>
  );

  return (
    <CustomDialog
      isDismissable
      styles={style({
        maxWidth: "90vw",
        width: {
          default: "full",
          sm: 1200,
        },
      })}
    >
      {({ close }) => (
        <div
          className={style({
            size: "full",
            display: "grid",
            gridTemplateAreas: [
              "header header header",
              ". . .",
              "content content content",
              ". . .",
              "footer footer buttonGroup",
            ],
            gridTemplateRows: ["auto", 16, "1fr", 32, "auto"],
          })}
        >
          <Header
            styles={style({
              gridArea: "header",
              display: "flex",
              alignItems: "center",
              gap: 12,
              font: "title-2xl",
              marginTop: 0,
            })}
          >
            <Text>Select brand</Text>
            <TooltipTrigger>
              <ActionButton
                aria-label="Refresh brands"
                onPress={listData.reload}
                isQuiet
                isDisabled={listData.isLoading}
                size="S"
              >
                <RefreshIcon />
              </ActionButton>
              <Tooltip>Refresh brands</Tooltip>
            </TooltipTrigger>
          </Header>
          {/* <Content styles={style({ gridArea: "content", overflowY: "auto" })}> */}
          <CardView
            selectionMode="single"
            selectionStyle="highlight"
            selectedKeys={listData.selectedKeys}
            onSelectionChange={handleSelectionChange}
            loadingState={listData.loadingState}
            renderEmptyState={renderEmptyState}
            variant="quiet"
            styles={style({
              gridArea: "content",
              maxHeight: "60vh",
              overflowY: "auto",
            })}
          >
            <Collection items={listData.items}>
              {(item) =>
                item.name !== "/NEW" ? (
                  <BrandCard item={item} />
                ) : (
                  <Card id={item.id} textValue={item.name}>
                    <CardPreview>{renderCreateNewBrand()}</CardPreview>
                  </Card>
                )
              }
            </Collection>
            {listData.isLoading && (
              <SkeletonCollection>
                {() => (
                  <BrandCard
                    item={{ id: Math.random(), name: "Loading...", url: "" }}
                  />
                )}
              </SkeletonCollection>
            )}
          </CardView>
          {/* </Content> */}
          <Footer styles={style({ gridArea: "footer" })}></Footer>
          <ButtonGroup
            styles={style({
              gridArea: "buttonGroup",
              justifySelf: "end",
            })}
          >
            <Button
              variant="accent"
              isDisabled={listData.selectedKeys.size === 0}
              onPress={() => {
                onPrimaryAction?.();
                close();
              }}
            >
              <Text>Select brand</Text>
            </Button>
          </ButtonGroup>
          <CloseButton
            styles={style({
              position: "absolute",
              top: 12,
              insetEnd: 12,
            })}
          />
        </div>
      )}
    </CustomDialog>
  );
};

BrandCard.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    url: PropTypes.string.isRequired,
  }).isRequired,
};

StoryBrandDialog.propTypes = {
  storyId: PropTypes.string.isRequired,
  onPrimaryAction: PropTypes.func,
};
